import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { api, download } from "@/api/client";
import type { SharedRender } from "@/api/types";
import { matchPrefix, useRouter } from "@/app/router";
import { Button, Card, Spinner } from "@/components/ui";
import { useToast, errorMessage } from "@/components/Toast";
import { SummaryTable, DetailedTable, WeeklyTable } from "./tables";

export function SharedReportPage() {
  const { path } = useRouter();
  const { toast } = useToast();
  const token = matchPrefix(path, "shared") ?? "";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shared", token],
    queryFn: () => api.get<SharedRender>(`/shared/${token}`),
    retry: false,
  });

  const exportFile = (format: "csv" | "xlsx") => {
    download(`/shared/${token}?format=${format}`).catch((err) => toast(errorMessage(err), "error"));
  };

  return (
    <div className="mx-auto min-h-full max-w-4xl px-6 py-10">
      <div className="mb-6 flex items-center gap-2.5">
        <span className="size-3 rounded-full bg-running" />
        <span className="text-sm font-semibold tracking-tight">Timer</span>
        <span className="text-sm text-faint">· shared report</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : isError || !data ? (
        <Card className="p-10 text-center text-sm text-muted">This shared report is unavailable or has been revoked.</Card>
      ) : (
        <div>
          <div className="mb-4 flex items-end justify-between">
            <h1 className="text-xl font-semibold tracking-tight">{data.name}</h1>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => exportFile("csv")}><Download className="size-4" /> CSV</Button>
              <Button variant="secondary" size="sm" onClick={() => exportFile("xlsx")}><Download className="size-4" /> Excel</Button>
            </div>
          </div>
          {data.summary && <SummaryTable r={data.summary} groupLabel={data.summary.group1} group2Label={data.summary.group2} />}
          {data.detailed && <DetailedTable r={data.detailed} />}
          {data.weekly && <WeeklyTable r={data.weekly} />}
        </div>
      )}
    </div>
  );
}
